import { useCallback, useEffect, useState } from 'react';
import {
  Loader,
  AlertCircle,
  Calendar,
  Plus,
  Unlink,
  X,
  ChevronDown,
  ChevronUp,
  Globe,
  CalendarClock,
  PartyPopper,
  Briefcase,
} from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { eventTeamApiService } from '../../services/eventTeamApi';
import { calendarApiService } from '../../services/calendarApi';
import type { CalendarEvent } from '../../types/calendar';
import type { EventCalendarReverseLink } from '../../types/eventTeam';
import type { EventTeamPerms } from '../../utils/eventTeamPermissions';
import { cn } from '../../utils/cn';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';

export interface EventCalendarSectionProps {
  targetType: 'event' | 'project';
  targetId: string;
  perms: EventTeamPerms;
  refreshNonce?: number;
  onChanged?: () => void;
}

/**
 * Calendar links (guide §1.4): list/link/unlink calendar events, upcoming subset,
 * and per-row reverse lookup ("also linked to" other events/projects).
 */
export const EventCalendarSection = ({
  targetType,
  targetId,
  perms,
  refreshNonce = 0,
  onChanged,
}: EventCalendarSectionProps) => {
  const navigate = useNavigate();
  const [linked, setLinked] = useState<CalendarEvent[]>([]);
  const [upcomingIds, setUpcomingIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Link-existing picker
  const [showPicker, setShowPicker] = useState(false);
  const [candidates, setCandidates] = useState<CalendarEvent[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerFilter, setPickerFilter] = useState('');

  // Reverse lookup expando
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [reverseLinks, setReverseLinks] = useState<EventCalendarReverseLink[] | null>(null);
  const [reverseLoading, setReverseLoading] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [all, upcoming] = await Promise.all([
        eventTeamApiService.listCalendarEvents(targetType, targetId),
        eventTeamApiService.listUpcomingCalendarEvents(targetType, targetId).catch(() => [] as CalendarEvent[]),
      ]);
      setLinked(all);
      setUpcomingIds(new Set(upcoming.map((e) => e.id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load calendar links');
    } finally {
      setLoading(false);
    }
  }, [targetId, targetType]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    if (refreshNonce === 0) return;
    void load();
  }, [refreshNonce, load]);

  const openPicker = async () => {
    setShowPicker(true);
    setPickerLoading(true);
    try {
      const events = await calendarApiService.getAllEvents();
      setCandidates(Array.isArray(events) ? events : []);
    } catch {
      setCandidates([]);
    } finally {
      setPickerLoading(false);
    }
  };

  const handleLink = async (calendarEventId: string) => {
    setBusyId(calendarEventId);
    try {
      await eventTeamApiService.linkCalendarEvent(targetType, targetId, calendarEventId);
      setShowPicker(false);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to link calendar event');
    } finally {
      setBusyId(null);
    }
  };

  const handleUnlink = async (calendarEventId: string) => {
    if (!confirm('Unlink this calendar event?')) return;
    setBusyId(calendarEventId);
    try {
      await eventTeamApiService.unlinkCalendarEvent(targetType, targetId, calendarEventId);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to unlink calendar event');
    } finally {
      setBusyId(null);
    }
  };

  const toggleReverse = async (calendarEventId: string) => {
    if (expandedId === calendarEventId) {
      setExpandedId(null);
      setReverseLinks(null);
      return;
    }
    setExpandedId(calendarEventId);
    setReverseLinks(null);
    setReverseLoading(true);
    try {
      const links = await eventTeamApiService.getTargetsForCalendarEvent(calendarEventId);
      setReverseLinks(links);
    } catch {
      setReverseLinks([]);
    } finally {
      setReverseLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-2" aria-busy="true">
        <SkeletonLoader className="h-4 w-32" />
        {Array.from({ length: 3 }).map((_, i) => (
          <SkeletonLoader key={i} className="h-11 w-full" />
        ))}
      </div>
    );
  }

  const linkedIds = new Set(linked.map((e) => e.id));
  const filteredCandidates = candidates.filter(
    (c) =>
      !linkedIds.has(c.id) &&
      (pickerFilter === '' || c.title.toLowerCase().includes(pickerFilter.toLowerCase())),
  );

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Calendar"
        icon={Calendar}
        iconClassName="text-cyan-400"
        count={linked.length}
        className="mb-0 flex-wrap"
        actions={
          perms.canCreate ? (
            <button
              type="button"
              onClick={() => (showPicker ? setShowPicker(false) : void openPicker())}
              className="glass-button-sm"
              aria-expanded={showPicker}
            >
              <Plus className="w-3.5 h-3.5" />
              Link calendar event
            </button>
          ) : undefined
        }
      />

      {error && (
        <div className="alert alert-danger" role="alert">
          <AlertCircle />
          <span className="flex-1">{error}</span>
        </div>
      )}

      {showPicker && (
        <div className="subcard p-3 space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={pickerFilter}
              onChange={(e) => setPickerFilter(e.target.value)}
              placeholder="Filter calendar events…"
              className="glass-input text-sm flex-1"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPicker(false)}
              className="btn-icon btn-icon-sm"
              title="Close"
              aria-label="Close picker"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          {pickerLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader className="w-5 h-5 animate-spin text-accent-400" />
            </div>
          ) : filteredCandidates.length === 0 ? (
            <p className="text-xs text-glass-500 py-2 text-center">No unlinked calendar events found.</p>
          ) : (
            <div className="space-y-1 max-h-[240px] overflow-y-auto">
              {filteredCandidates.map((c) => (
                <button
                  key={c.id}
                  onClick={() => void handleLink(c.id)}
                  disabled={busyId === c.id}
                  className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-left hover:bg-glass-200/10 transition-colors"
                >
                  <div className="min-w-0">
                    <span className="text-sm text-white truncate block">{c.title}</span>
                    <span className="text-[11px] text-glass-500 tabular-nums">
                      {format(new Date(c.start_time), 'MMM d yyyy, HH:mm')}
                    </span>
                  </div>
                  {busyId === c.id ? (
                    <Loader className="w-3.5 h-3.5 animate-spin text-glass-400" />
                  ) : (
                    <Plus className="w-3.5 h-3.5 text-accent-400 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {linked.length === 0 && !error ? (
        <EmptyState bare compact icon={Calendar} title="No calendar events linked" />
      ) : (
        <div className="space-y-2">
          {linked.map((calEvent) => {
            const isUpcoming = upcomingIds.has(calEvent.id);
            const busy = busyId === calEvent.id;
            const expanded = expandedId === calEvent.id;
            return (
              <div key={calEvent.id} className="subcard">
                <div className="flex items-center justify-between gap-2 px-3 py-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <button
                      type="button"
                      onClick={() => navigate('/calendar')}
                      className="text-sm text-accent-300 hover:text-accent-200 hover:underline underline-offset-2 transition-colors font-medium truncate"
                      title="Open the calendar"
                    >
                      {calEvent.title}
                    </button>
                    <span className="text-xs text-glass-400 inline-flex items-center gap-1 tabular-nums">
                      <CalendarClock className="w-3 h-3" />
                      {format(new Date(calEvent.start_time), 'MMM d yyyy, HH:mm')}
                      {calEvent.end_time && ` → ${format(new Date(calEvent.end_time), 'HH:mm')}`}
                    </span>
                    {isUpcoming && (
                      <span className="badge bg-cyan-500/15 text-cyan-300 border-cyan-500/25">upcoming</span>
                    )}
                    {calEvent.publish && (
                      <span className="badge bg-emerald-500/15 text-emerald-300 border-emerald-500/25">
                        <Globe className="w-2.5 h-2.5" />
                        public
                      </span>
                    )}
                    <span
                      className={cn(
                        'badge badge-dot capitalize',
                        calEvent.status === 'active'
                          ? 'badge-success'
                          : calEvent.status === 'cancelled'
                            ? 'badge-danger'
                            : 'badge-amber',
                      )}
                    >
                      {calEvent.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => void toggleReverse(calEvent.id)}
                      className="btn-icon btn-icon-sm"
                      title="Show other events/projects linked to this calendar event"
                      aria-label="Show other links for this calendar event"
                      aria-expanded={expanded}
                    >
                      {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>
                    {perms.canCreate && (
                      <button
                        type="button"
                        onClick={() => void handleUnlink(calEvent.id)}
                        disabled={busy}
                        className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                        title="Unlink"
                        aria-label="Unlink calendar event"
                      >
                        {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Unlink className="w-3.5 h-3.5" />}
                      </button>
                    )}
                  </div>
                </div>

                {expanded && (
                  <div className="px-3 pb-2 border-t border-glass-200/10 pt-2">
                    <p className="eyebrow mb-1">Also linked to</p>
                    {reverseLoading ? (
                      <Loader className="w-4 h-4 animate-spin text-glass-400" />
                    ) : !reverseLinks || reverseLinks.length === 0 ? (
                      <p className="text-xs text-glass-500">Nothing else links to this calendar event.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {reverseLinks.map((link) => (
                          <button
                            key={`${link.target_type}-${link.target_id}`}
                            onClick={() =>
                              navigate(
                                link.target_type === 'event'
                                  ? `/event-team/events/${link.target_id}`
                                  : `/event-team/projects/${link.target_id}`,
                              )
                            }
                            className={cn(
                              'chip transition-colors',
                              link.target_id === targetId && link.target_type === targetType
                                ? 'bg-accent-500/15 text-accent-300 border-accent-500/25 cursor-default'
                                : 'hover:text-white hover:border-glass-200/40',
                            )}
                          >
                            {link.target_type === 'event' ? (
                              <PartyPopper className="w-3 h-3 text-accent-400" />
                            ) : (
                              <Briefcase className="w-3 h-3 text-blue-400" />
                            )}
                            {link.title || link.target_id}
                            {link.target_id === targetId && link.target_type === targetType && ' (this)'}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
